import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { apptEnums, checkEditPermission } from './constants';
import { apptActionNames, checkCreateApptPermissions, SERVICE, UNAVAILABLE } from '../constants';
import { Colors, FindLoad, FindSuccessItem, Images, PermissionList, RolePermission, useModal, User, useSecondaryModal } from 'utils';
import { CustomDelete, MinLoader, SimpleModal, SimpleTooltip } from 'components';
import { appointmentActions, httpRequestsOnSuccessActions } from 'store';
import { Recur, CreateEvent } from './modals';
import { appointmentService } from '../../../store/appointment/appointment.service';

const CONNECT_RECONNECT = 'CONNECT_RECONNECT_TEMPLATE';
const DELETE_TYPE = 'DELETE_APPOINTMENT';
const LOCK_UNLOCK_ACTION_TYPE = 'CHANGE_APPOINTMENT_LOCK_UNLOCK';
const CHECK_ACTION_TYPE = 'CHECK_ELIGIBILITY';
export const ApptActionsWithModals = ({ item, afterSuccess, fromTable }) => {
    const canUseIntegrations = useSelector((state) => state.system.canUseIntegrations);
    const dispatch = useDispatch();
    const { open } = useModal();
    const { closeSecondary } = useSecondaryModal();
    const { userInfo } = User();
    const [editAppt, setEditAppt] = useState(null);
    const [loadInfo, setLoadInfo] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const successCheck = FindSuccessItem(CHECK_ACTION_TYPE);
    const loadEligibility = FindLoad(CHECK_ACTION_TYPE);
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? 'simple-popover' : undefined;
    const checkDelete = item?.locked
        ? RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) &&
          (RolePermission([PermissionList.APPT_SELF_DELETE?.code])
              ? userInfo?.id === item?.staff?.id
              : RolePermission([PermissionList.UNRENDERED_APPT_DELETE?.code]))
        : RolePermission([PermissionList.UNRENDERED_APPT_DELETE?.code]);

    const handleOpenRecur = (item) => {
        open(<Recur apptId={item?.id} />);
    };

    const deleteAppt = () => {
        open(
            <CustomDelete
                text={`Are you sure you want to delete this appointment?`}
                info="Delete appointment"
                handleDel={() => dispatch(appointmentActions.deleteAppointment(item?.id))}
                actionType={DELETE_TYPE}
                afterSuccess={() => {
                    closeSecondary();
                    afterSuccess();
                }}
            />
        );
    };

    const handleLockUnlock = (item) => {
        const subText = item?.locked
            ? 'Are you sure you want to unlock this appointment? Once unlocked, it can be edited again.'
            : 'Are you sure you want to lock this appointment? Once locked, it cannot be edited until unlocked.';

        open(
            <CustomDelete
                info={!item?.locked ? 'Confirm Lock Action' : 'Confirm Unlock Action'}
                innerText={!item?.locked ? 'Lock' : 'Unlock'}
                text={subText}
                color={'blue'}
                handleDel={() =>
                    dispatch(
                        appointmentActions.changeApptLockUnlock(
                            {
                                apptIds: [item?.id],
                                all: false,
                            },
                            !item?.locked,
                            {},
                            item?.id
                        )
                    )
                }
                actionType={LOCK_UNLOCK_ACTION_TYPE}
                afterSuccess={afterSuccess}
            />
        );
    };

    const connectReconnect = (item) => {
        const checkConnect = !item?.template;
        const subText = checkConnect
            ? "This appointment was previously disconnected from its recurring group. Reconnecting it will associate it back with the group and align it with the group's recurring pattern."
            : 'Are you sure you want to disconnect this appointment from its recurring group? Disconnection will make this appointment independent of the group. You can always reconnect it later if needed.';

        open(
            <CustomDelete
                info={checkConnect ? 'Reconnect Appointment to Group' : 'Disconnect Appointment from Group'}
                text={subText}
                innerText={checkConnect ? 'Reconnect' : 'Disconnect'}
                handleDel={() => dispatch(appointmentActions.connectReconnect(item?.id, { id: item?.template }))}
                actionType={CONNECT_RECONNECT}
                color={'blue'}
                afterSuccess={afterSuccess}
            />
        );
    };

    const handleOpenClose = () => {
        setEditAppt(null);
    };

    const getApptById = (item, name) => {
        setLoadInfo({
            id: item?.id,
            from: name,
        });
        appointmentService
            .getAppointmentByIdService(item?.id)
            .then((res) => {
                const params = {
                    ...res?.data,
                };

                if (name === apptActionNames.DUPLICATE) {
                    params.actionType = apptActionNames.DUPLICATE;
                    delete params.startDate;
                    delete params.endDate;
                    delete params.startTime;
                    delete params.endTime;
                } else {
                    delete params.actionType;
                }

                setEditAppt(params);
                setAnchorEl(null);
            })
            .finally(() => {
                setLoadInfo(null);
            });
    };
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCheckEligibility = () => {
        setLoadInfo({
            id: item?.id,
            from: apptActionNames.CHECK,
        });
        const apptId = !fromTable ? item?.id : null;
        dispatch(appointmentActions.checkEligibility(item?.client?.id, apptId));
    };

    useEffect(() => {
        if (successCheck) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CHECK_ACTION_TYPE));
            handleClose();
        }
    }, [successCheck]);

    return (
        <div>
            {fromTable ? (
                <div className="flex-align-center" style={{ gap: 4 }}>
                    <div className="appt-action-btn">
                        {RolePermission([PermissionList.APPT_LOCK?.code]) &&
                            item?.status !== apptEnums.CANCELLED &&
                            item?.type !== UNAVAILABLE && (
                                <SimpleTooltip
                                    title={<p>{item?.locked ? 'Unlock Appointment' : 'Lock Appointment'}</p>}
                                    placement="top-end"
                                >
                                    <button className="new-action-btn-box-small" onClick={(e) => handleLockUnlock(item)}>
                                        <img src={item?.locked ? Images.lock : Images.unlock} alt="icon" />
                                    </button>
                                </SimpleTooltip>
                            )}
                    </div>
                    <button className="new-action-btn-box-small" onClick={handleClick}>
                        <img src={Images.moreGrayIcon} alt="icon" />
                    </button>
                    <Popover
                        id={id}
                        open={openPopper}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                    >
                        <div className="appt-action-popover">
                            <div className="full-width">
                                {checkEditPermission(item, userInfo) && (
                                    <button
                                        style={
                                            RolePermission([PermissionList.LOCKED_APPT_EDIT?.code])
                                                ? {}
                                                : item?.locked
                                                  ? { opacity: 0.5 }
                                                  : {}
                                        }
                                        disabled={RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) ? false : item?.locked}
                                        onClick={() => getApptById(item, apptActionNames.EDIT)}
                                    >
                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            {loadInfo?.id === item?.id && loadInfo?.from === apptActionNames.EDIT ? (
                                                <div style={{ width: 24 }}>
                                                    <MinLoader width={'20px'} margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                                                </div>
                                            ) : (
                                                <img src={Images.editDarkBlue} alt="icon" />
                                            )}
                                            <p>Edit</p>
                                        </div>
                                    </button>
                                )}
                            </div>
                            <div className="full-width">
                                {RolePermission([PermissionList.APPT_RECURE?.code]) &&
                                !item?.template &&
                                item?.status !== apptEnums.CANCELLED ? (
                                    <button
                                        onClick={() => {
                                            handleOpenRecur(item);
                                            handleClose();
                                        }}
                                    >
                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            <img src={Images.repeatAppt} alt="icon" />
                                            <p>Recur</p>
                                        </div>
                                    </button>
                                ) : (
                                    item?.template &&
                                    item?.status !== apptEnums.CANCELLED && (
                                        <button
                                            className="new-action-btn-box-small"
                                            onClick={() => {
                                                connectReconnect(item);
                                                handleClose();
                                            }}
                                        >
                                            <img src={item?.template ? Images.reConnectedTemplate : Images.connectedTemplate} alt="icon" />
                                            <p>Disconnect from Group</p>
                                        </button>
                                    )
                                )}
                            </div>
                            {checkCreateApptPermissions(userInfo) && (
                                <div className="full-width">
                                    <button onClick={() => getApptById(item, apptActionNames.DUPLICATE)}>
                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            {loadInfo?.id === item?.id && loadInfo.from === apptActionNames.DUPLICATE ? (
                                                <div style={{ width: 24 }}>
                                                    <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                                                </div>
                                            ) : (
                                                <img src={Images.duplicate} alt="icon" />
                                            )}
                                            <p>Duplicate</p>
                                        </div>
                                    </button>
                                </div>
                            )}

                            {
                                // (!item?.client?.eligibilityCheck || !item?.client?.eligibilityCheck?.status) &&
                                RolePermission([PermissionList.ELIGIBILITY_CHECK?.code]) &&
                                    // canUseIntegrations?.activeOfficeAllySOAP &&
                                    canUseIntegrations?.activeTlClearinghouse &&
                                    item?.type === SERVICE &&
                                    item?.status !== apptEnums.CANCELLED && (
                                        // (canUseIntegrations?.activeSOAP && item?.type === SERVICE && item?.status !== apptEnums.CANCELLED) &&

                                        <div className="full-width">
                                            <button
                                                onClick={(e) => {
                                                    handleCheckEligibility(e);
                                                }}
                                            >
                                                {loadInfo?.id === item?.id &&
                                                loadInfo?.from === apptActionNames.CHECK &&
                                                loadEligibility?.length ? (
                                                    <div style={{ width: 24 }}>
                                                        <MinLoader
                                                            width={'20px'}
                                                            margin={'0'}
                                                            color={Colors.ThemeBlue}
                                                            position={'relative'}
                                                        />
                                                    </div>
                                                ) : (
                                                    <img src={Images.userCheckEligibility} alt="icon" />
                                                )}

                                                <p>Check Eligibility</p>
                                            </button>
                                        </div>
                                    )
                            }

                            <div className="full-width">
                                {(item?.status === apptEnums.PENDING || item?.status === apptEnums.CANCELLED) && checkDelete && (
                                    <button
                                        onClick={(e) => {
                                            deleteAppt(e);
                                            handleClose();
                                        }}
                                    >
                                        <img src={Images.deleteTag} alt="icon" />
                                        <p>Delete</p>
                                    </button>
                                )}
                            </div>
                        </div>
                    </Popover>
                </div>
            ) : (
                <div className="appt-actions-box">
                    {checkEditPermission(item, userInfo) && (
                        <button
                            className="new-action-btn-box-small"
                            style={RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) ? {} : item?.locked ? { opacity: 0.5 } : {}}
                            disabled={RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) ? false : item?.locked}
                            onClick={() => getApptById(item, apptActionNames.EDIT)}
                        >
                            {loadInfo?.id === item?.id && loadInfo?.from === apptActionNames.EDIT ? (
                                <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                            ) : (
                                <img src={Images.editDarkBlue} alt="icon" />
                            )}
                        </button>
                    )}

                    {RolePermission([PermissionList.APPT_RECURE?.code]) && !item?.template && item?.status !== apptEnums.CANCELLED ? (
                        <SimpleTooltip title={<p>{'Recur Event'}</p>} placement="top-end">
                            <button className="new-action-btn-box-small" onClick={(e) => handleOpenRecur(item)}>
                                <img src={Images.repeatAppt} alt="icon" />
                            </button>
                        </SimpleTooltip>
                    ) : (
                        item?.template &&
                        item?.status !== apptEnums.CANCELLED && (
                            <SimpleTooltip
                                title={<p>{item?.template ? 'Disconnect from Group' : 'Reconnect from Group'}</p>}
                                placement="top-end"
                            >
                                <button className="new-action-btn-box-small" onClick={() => connectReconnect(item)}>
                                    <img src={item?.template ? Images.reConnectedTemplate : Images.connectedTemplate} alt="icon" />
                                </button>
                            </SimpleTooltip>
                        )
                    )}

                    {RolePermission([PermissionList.APPT_LOCK?.code]) &&
                        item?.status !== apptEnums.CANCELLED &&
                        item?.type !== UNAVAILABLE && (
                            <SimpleTooltip title={<p>{item?.locked ? 'Unlock Appointment' : 'Lock Appointment'}</p>} placement="top-end">
                                <button className="new-action-btn-box-small" onClick={(e) => handleLockUnlock(item)}>
                                    <img src={item?.locked ? Images.lock : Images.unlock} alt="icon" />
                                </button>
                            </SimpleTooltip>
                        )}

                    {(item?.status === apptEnums.PENDING || item?.status === apptEnums.CANCELLED) && checkDelete && (
                        <SimpleTooltip title={<p>{'Delete Appointment'}</p>} placement="top-end">
                            <button className="new-action-btn-box-small" onClick={(e) => deleteAppt(e)}>
                                <img src={Images.deleteTag} alt="icon" />
                            </button>
                        </SimpleTooltip>
                    )}
                </div>
            )}

            <SimpleModal
                handleOpenClose={handleOpenClose}
                openDefault={editAppt}
                content={<CreateEvent afterSuccess={afterSuccess} modalDate={editAppt} handleOpenClose={handleOpenClose} />}
            />
        </div>
    );
};
